import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { seoApi } from "@/lib/seoApi";
import { FileText, Sparkles, Calendar, Search, CircleCheck, CircleAlert, Plus } from "lucide-react";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 text-neutral-500 mb-2">
          <Icon size={16} /> <span className="text-xs uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-3xl font-semibold">{value ?? "—"}</div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState({});
  const [recent, setRecent] = useState([]);
  const [health, setHealth] = useState(null);
  const [quality, setQuality] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    const g = async (url, setter) => {
      try { const r = await seoApi.get(url); setter(r.data); }
      catch (e) { console.warn(`[dashboard] ${url} failed:`, e?.response?.status, e?.message); }
    };
    (async () => {
      const st = {};
      await Promise.all([
        g("/seo/briefs?limit=500", (d) => (st.briefs = d.length)),
        g("/seo/content-plans?limit=500", (d) => (st.plans = d.length)),
        g("/seo/keywords?limit=500", (d) => (st.keywords = d.length)),
        g("/seo/intents?limit=500", (d) => (st.intents = d.length)),
      ]);
      setStats(st);
      await g("/seo/workflows/runs?limit=10", setRecent);
      await g("/seo/workflows/review-queue", (d) => setReviewCount(d?.count ?? 0));
      await g("/acf/observability/health", setHealth);
      await g("/seo/stats/quality?days=90", setQuality);
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="seo-dashboard">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-neutral-500 text-sm mt-1">Overview of SEO Studio + AI Content Factory.</p>
        </div>
        <Link to="/seo-studio/briefs/new">
          <Button data-testid="dashboard-new-brief">
            <Plus size={16} className="mr-1" /> New Brief
          </Button>
        </Link>
      </div>

      {reviewCount > 0 && (
        <Link to="/seo-studio/review" className="block" data-testid="review-queue-banner">
          <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 flex items-center justify-between hover:bg-orange-100 transition-colors">
            <div className="flex items-center gap-2 text-sm text-orange-800">
              <CircleAlert size={16} />
              <b>{reviewCount}</b> article(s) blocked by the Uniqueness Gate — waiting for review
            </div>
            <span className="text-xs text-orange-700 underline">Open Review Queue →</span>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="stat-briefs" icon={FileText} label="Briefs" value={stats.briefs} />
        <StatCard testid="stat-plans" icon={Calendar} label="Content Plans" value={stats.plans} />
        <StatCard testid="stat-keywords" icon={Search} label="Keywords" value={stats.keywords} />
        <StatCard testid="stat-intents" icon={Sparkles} label="Search Intents" value={stats.intents} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="recent-runs-card">
          <CardHeader>
            <CardTitle className="text-base">Recent Generation Runs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {recent.length === 0 ? (
              <div className="text-sm text-neutral-500 px-6 pb-6">
                No generation runs yet. Create a brief and click &quot;Generate via ACF&quot;.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-neutral-500">
                  <tr className="border-t border-neutral-200">
                    <th className="text-left px-6 py-2">Status</th>
                    <th className="text-left px-4 py-2">Brief</th>
                    <th className="text-left px-4 py-2">Provider</th>
                    <th className="text-left px-4 py-2">Started</th>
                    <th className="px-6 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} className="border-t border-neutral-100">
                      <td className="px-6 py-3">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {r.brief_id ? r.brief_id.slice(0, 8) : "—"} v{r.brief_version ?? "—"}
                      </td>
                      <td className="px-4 py-3">{r.provider || "—"} / {r.model || "—"}</td>
                      <td className="px-4 py-3 text-neutral-500">
                        {new Date(r.started_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <Link to={`/seo-studio/runs/${r.id}`}
                              className="text-sm text-neutral-900 underline hover:no-underline">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card data-testid="health-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              System status
              {health?.status === "healthy" ? (
                <CircleCheck size={16} className="text-emerald-600" />
              ) : health ? (
                <CircleAlert size={16} className="text-amber-600" />
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">Overall</span>
              <span className="font-medium capitalize">{health?.status || "…"}</span>
            </div>
            {(health?.components || []).map((c) => (
              <div key={c.component} className="flex justify-between border-t border-neutral-100 pt-2">
                <span className="text-neutral-500">{c.component}</span>
                <span className="capitalize">{c.status}</span>
              </div>
            ))}
            <div className="pt-3 text-xs text-neutral-500">
              Live from <code>/api/acf/observability/health</code>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quality signals — anonymous, aggregated from existing runs.
         Purely informational; helps decide where to focus future
         quality iterations based on real usage. */}
      {quality && quality.successful_runs > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quality-signals">
          <Card>
            <CardHeader><CardTitle className="text-base">Quality signals (last 90 days)</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                <QS label="Runs" value={quality.successful_runs} />
                <QS label="Avg score" value={quality.avg_quality_score != null
                  ? `${Math.round(quality.avg_quality_score * 100)}%` : "—"} />
                <QS label="Publish rate" value={`${quality.publish_funnel?.publish_rate_pct ?? 0}%`} />
              </div>
              <div className="text-neutral-500 text-xs uppercase tracking-wide">Top warnings</div>
              {(quality.warnings_by_frequency || []).slice(0, 6).map((w, i) => (
                <div key={i} className="flex justify-between border-t border-neutral-100 pt-2">
                  <span className="text-neutral-700 pr-4 truncate" title={w.warning}>{w.warning}</span>
                  <span className="text-neutral-500 font-mono whitespace-nowrap">
                    {w.count} · {w.pct_of_runs}%
                  </span>
                </div>
              ))}
              {(quality.warnings_by_frequency || []).length === 0 && (
                <div className="text-neutral-500 text-xs">No warnings recorded yet.</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">By provider · country · intent</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-3">
              <MiniBreakdown title="Provider / Model" rows={quality.by_provider_model} valueSuffix="%" />
              <MiniBreakdown title="Country" rows={quality.by_country} valueSuffix="%" />
              <MiniBreakdown title="Search intent" rows={quality.by_search_intent} valueSuffix="%" />
              <div className="text-xs text-neutral-500 border-t border-neutral-100 pt-2">
                Edits before publish: {quality.publish_funnel?.edited_before_publish ?? 0} · Edit rate: {quality.edit_stats?.edit_rate_pct ?? 0}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function QS({ label, value }) {
  return (
    <div className="bg-neutral-50 border border-neutral-100 rounded-md px-3 py-2">
      <div className="text-neutral-500 uppercase tracking-wide text-[10px]">{label}</div>
      <div className="text-lg font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function MiniBreakdown({ title, rows, valueSuffix }) {
  if (!rows || !rows.length) return null;
  return (
    <div>
      <div className="text-neutral-500 text-xs uppercase tracking-wide mb-1">{title}</div>
      {rows.slice(0, 4).map((r, i) => (
        <div key={i} className="flex justify-between text-xs py-1 border-b border-neutral-100 last:border-none">
          <span className="truncate pr-3">{r.key}</span>
          <span className="text-neutral-500 font-mono whitespace-nowrap">
            n={r.n} · {Math.round(r.avg_quality_score * 100)}{valueSuffix || ""}
          </span>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    success: "bg-emerald-100 text-emerald-800 border-emerald-200",
    running: "bg-amber-100 text-amber-800 border-amber-200",
    failed: "bg-rose-100 text-rose-800 border-rose-200",
  };
  return (
    <Badge variant="outline" className={map[status] || ""}>{status}</Badge>
  );
}
